import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  TextField,
  Typography,
  Divider,
  Grid,
  Select,
  MenuItem,
  SelectChangeEvent,
  CircularProgress,
  Tooltip,
  Button,
} from "@mui/material";
import axios from "axios";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useSelector } from "react-redux";
import Restrict from "../../Restrict";
import DynamicTable from "../../../components/lab-dynamic-table";
import { FileDownload, Print, Restore } from "@mui/icons-material";
import DateRangeSelector from "../../../components/DateRangeSelector";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import * as XLSX from "xlsx";
import { formatCurrency } from "../../../utils/numberUtils";

// --- TYPE DEFINITIONS ---
interface DeletedIncomeRecord {
  _id: string;
  subTotal: number;
  discount: number;
  grandTotal: number;
  payment: number;
  due: number;
  deletedAt: string;
  advance: number;
  deletedBy:string
  reason?: string; // <-- Added the optional reason field
  patientInfo: {
    patientName: string;
    mrn: number;
    guardiansName: string;
    phonNumber: string;
    gender: string;
  };
  createdBy: {
    _id: string;
    fullName: string;
  };
  deleteReason?: string; // <-- Added the optional deleteReason field
}

// --- HELPER FUNCTION ---
const formatIndianNumber = (num: number): string => {
  if (isNaN(num)) return "0";
  const numStr = Math.floor(num).toString();
  const len = numStr.length;
  if (len <= 3) return numStr;
  let result = "";
  let count = 0;
  for (let i = len - 1; i >= 0; i--) {
    result = numStr[i] + result;
    if (count % 2 === 1 && count < 5 && i > 0) {
      result = "," + result;
    }
    count++;
  }
  return result;
};

// --- MAIN COMPONENT ---
const DeletedIncome: React.FC = () => {
  const nowDate = new Date().toISOString().split("T")[0];

  // --- STATE MANAGEMENT ---
  const [deletedIncomes, setDeletedIncomes] = useState<DeletedIncomeRecord[]>(
    []
  );
  const [loading, setLoading] = useState<boolean>(true);
  const [dateRange, setDateRange] = useState<string>("Today");
  const [fromDate, setFromDate] = useState<string>(nowDate);
  const [toDate, setToDate] = useState<string>(nowDate);

  // --- REDUX & AUTH ---
  const profile = useSelector((state: any) => state.profileSlice.profile);
  const token = localStorage.getItem("token");

  // --- API DATA FETCHING ---
  const fetchDeletedIncomes = useCallback(async (): Promise<void> => {
    if (!fromDate || !toDate) return;

    setLoading(true);
    try {
      const url = `${process.env.REACT_APP_ADMIN_API_URL}/income/getDeletedIncome`;
      const params = new URLSearchParams({ fromDate, toDate });
      const response = await axios.get(`${url}?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.data.isSuccess) {
        const records = response.data.data || [];
        setDeletedIncomes(records);
      } else {
        toast.error(response.data.message || "Failed to fetch deleted records");
        setDeletedIncomes([]);
      }
    } catch (err: any) {
      console.error("Fetch Deleted Incomes Error:", err.response || err);
      toast.error(
        err.response?.data?.message ||
          "An error occurred while fetching records"
      );
      setDeletedIncomes([]);
    } finally {
      setLoading(false);
    }
  }, [fromDate, toDate, token]);

  // --- EFFECT TO FETCH ON DATE CHANGE ---
  useEffect(() => {
    fetchDeletedIncomes();
  }, [fetchDeletedIncomes]);

  const handleRestoreIncome = async (rowIndex: number): Promise<void> => {
    const record = deletedIncomes?.[rowIndex];
    if (!record?._id) {
      toast.error("Invalid record selected");
      return;
    }

    setLoading(true);
    try {
      const url = `${process.env.REACT_APP_ADMIN_API_URL}/income/restoreDeletedIncome/${record._id}`;
      const response = await axios.put(
        url,
        {},
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      const { isSuccess, message } = response.data;
      if (isSuccess) {
        toast.success(message || "Income restored successfully");
        await fetchDeletedIncomes(); // Refresh after restore
      } else {
        toast.error(message || "Failed to restore income");
      }
    } catch (err: any) {
      console.error("Restore Income Error:", err.response || err);
      toast.error(
        err.response?.data?.message ||
          "An error occurred while restoring income"
      );
    } finally {
      setLoading(false);
    }
  };
  // --- HANDLER FOR DATE RANGE DROPDOWN ---
  const handleDateRangeChange = (value: string) => {
    setDateRange(value);
  };
  const formatDateTime = (date: Date | null) => {
    if (!date) return "";
    const pad = (n: number) => n.toString().padStart(2, "0");
    return `${date.getFullYear()}/${pad(date.getMonth() + 1)}/${pad(
      date.getDate()
    )} ${pad(date.getHours())}:${pad(date.getMinutes())}`;
  };

  // --- FUNCTION TO EXPORT DATA TO PDF ---
  const exportToPDF = () => {
    if (!deletedIncomes.length) return toast.warn("No data to print.");

    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 8;
    let yPosition = margin;

    // Professional header with minimal, clean design
    doc
      .setFontSize(10)
      .setFont("helvetica", "bold")
      .setTextColor(25, 55, 85)
      .text("DELETED INVOICE RECORDS", pageWidth / 2, yPosition, {
        align: "center",
      });
    
    yPosition += 6;
    doc
      .setDrawColor(25, 55, 85)
      .setLineWidth(0.5)
      .line(margin, yPosition, pageWidth - margin, yPosition);

    // Subtle metadata section with smaller font
    yPosition += 3;
    doc
      .setFontSize(6.5)
      .setFont("helvetica", "normal")
      .setTextColor(80, 80, 80)
      .text(
        `Report Period: ${fromDate} to ${toDate} | Generated: ${new Date().toLocaleDateString()} ${new Date().toLocaleTimeString()}`,
        pageWidth / 2,
        yPosition,
        { align: "center" }
      );

    yPosition += 6;

    const head = [
      [
        "SN",
        "MRN",
        "PATIENT NAME",
        "GUARDIAN",
        "GENDER",
        "SUB TOTAL",
        "DISCOUNT",
        "GRAND TOTAL",
        "REASON",
        "DUE",
        "DELETED BY",
        "DELETED AT",
      ],
    ];

    const body = deletedIncomes.map((record, index) => [
      index + 1,
      record.patientInfo?.mrn || "N/A",
      record.patientInfo?.patientName || "N/A",
      record.patientInfo?.guardiansName || "N/A",
      record.patientInfo?.gender || "N/A",
      formatCurrency(record.subTotal),
      formatCurrency(record.discount),
      formatCurrency(record.grandTotal),
      record?.deleteReason || "N/A",
      formatCurrency(record.due),
      record.deletedBy || "N/A",
      record.deletedAt ? formatDateTime(new Date(record.deletedAt)) : "N/A",
    ]);

    // Calculate totals
    const totalSubTotal = deletedIncomes.reduce((sum, item) => sum + (Number(item?.subTotal) || 0), 0);
    const totalDiscount = deletedIncomes.reduce((sum, item) => sum + (Number(item?.discount) || 0), 0);
    const totalGrandTotal = deletedIncomes.reduce((sum, item) => sum + (Number(item?.grandTotal) || 0), 0);
    const totalDue = deletedIncomes.reduce((sum, item) => sum + (Number(item?.due) || 0), 0);

    body.push([
      "",
      "",
      "",
      "",
      "TOTAL",
      formatCurrency(totalSubTotal),
      formatCurrency(totalDiscount),
      formatCurrency(totalGrandTotal),
      "",
      formatCurrency(totalDue),
      "",
      "",
    ]);

    // Clean, professional table styling
    autoTable(doc, {
      startY: yPosition,
      head,
      body,
      headStyles: { 
        fillColor: [25, 55, 85],
        textColor: [255, 255, 255],
        fontStyle: 'bold',
        fontSize: 6,
        halign: 'center',
        valign: 'middle',
        cellPadding: 2
      },
      bodyStyles: {
        fontSize: 5.5,
        textColor: [40, 40, 40],
        cellPadding: 1.8
      },
      columnStyles: {
        5: { halign: "right" },
        6: { halign: "right" },
        7: { halign: "right" },
        9: { halign: "right" },
      },
      alternateRowStyles: {
        fillColor: [248, 249, 250]
      },
      margin: { top: margin, right: margin, bottom: margin, left: margin },
      didParseCell: function(data) {
        // Professional total row with premium styling
        if (data.row.index === body.length - 1) {
          data.cell.styles.fillColor = [25, 55, 85];
          data.cell.styles.textColor = [255, 255, 255];
          data.cell.styles.fontStyle = 'bold';
          data.cell.styles.fontSize = 5.8;
          data.cell.styles.lineColor = [200, 200, 200];
          data.cell.styles.lineWidth = 0.3;
          (data.cell.styles as any).cellPadding = { top: 2.5, right: 2, bottom: 2.5, left: 2 };
        }
      },
      didDrawPage: function(data) {
        // Add subtle footer with page number
        const pageSize = doc.internal.pageSize;
        const pageCount = doc.getNumberOfPages();
        const pageNum = data.pageNumber;
        
        doc.setFontSize(5);
        doc.setTextColor(120, 120, 120);
        doc.text(
          `Page ${pageNum} of ${pageCount}`,
          pageSize.getWidth() / 2,
          pageSize.getHeight() - 4,
          { align: "center" }
        );
      }
    });

    doc.autoPrint();
    const blob = doc.output("blob");
    const blobUrl = URL.createObjectURL(blob);
    const iframe = document.createElement("iframe");
    iframe.style.position = "fixed";
    iframe.style.width = "0";
    iframe.style.height = "0";
    iframe.style.border = "none";
    document.body.appendChild(iframe);
    
    const cleanup = () => {
      if (document.body.contains(iframe)) {
        document.body.removeChild(iframe);
        URL.revokeObjectURL(blobUrl);
      }
      window.removeEventListener("focus", cleanup);
    };
    
    window.addEventListener("focus", cleanup);
    iframe.src = blobUrl;
    
    iframe.onload = () => {
      try {
        const win = iframe.contentWindow;
        if (win) {
          win.focus();
          win.print();
        }
      } catch (e) {
        console.error("Error triggering print dialog:", e);
        cleanup();
      }
    };
    
    // Fallback print trigger
    setTimeout(() => {
      try {
        const win = iframe.contentWindow;
        if (win && document.body.contains(iframe)) {
          win.focus();
          win.print();
        }
      } catch (e) {
        console.error("Fallback print attempt failed:", e);
        cleanup();
      }
    }, 1000);
  };

  // --- FUNCTION TO EXPORT DATA TO EXCEL ---
  const exportToExcel = () => {
    const worksheetData = deletedIncomes.map((record, index) => ({
      SN: index + 1,
      MRN: record.patientInfo?.mrn || "N/A",
      "Patient Name": record.patientInfo?.patientName || "N/A",
      Guardian: record.patientInfo?.guardiansName || "N/A",
      Gender: record.patientInfo?.gender || "N/A",
      "Sub Total": formatCurrency(record.subTotal),
      Discount: formatCurrency(record.discount),
      "Grand Total": formatCurrency(record.grandTotal),
      Reason: record?.deleteReason || "N/A",
      Due: formatCurrency(record.due),
      "Deleted By": record.deletedBy || "N/A",
      "Deleted At": record.deletedAt
        ? formatDateTime(new Date(record.deletedAt))
        : "N/A",
    }));

    // Calculate totals
    const totalSubTotal = deletedIncomes.reduce((sum, item) => sum + (Number(item?.subTotal) || 0), 0);
    const totalDiscount = deletedIncomes.reduce((sum, item) => sum + (Number(item?.discount) || 0), 0);
    const totalGrandTotal = deletedIncomes.reduce((sum, item) => sum + (Number(item?.grandTotal) || 0), 0);
    const totalDue = deletedIncomes.reduce((sum, item) => sum + (Number(item?.due) || 0), 0);

    // Add total row
    worksheetData.push({
      SN: "" as any,
      MRN: "",
      "Patient Name": "",
      Guardian: "",
      Gender: "TOTAL",
      "Sub Total": formatCurrency(totalSubTotal),
      Discount: formatCurrency(totalDiscount),
      "Grand Total": formatCurrency(totalGrandTotal),
      Reason: "",
      Due: formatCurrency(totalDue),
      "Deleted By": "",
      "Deleted At": "",
    });

    const worksheet = XLSX.utils.json_to_sheet(worksheetData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Deleted Income Records");
    XLSX.writeFile(workbook, "DeletedIncomeRecords.xlsx");
  };
  const gradientButtonStyle = {
    minWidth: { xs: "auto", md: "80px" },
    fontSize: { xs: "12px", md: "12px" },
    fontWeight: "600",
    textTransform: "none",
    borderRadius: "8px",
    background: "linear-gradient(45deg, #1976d2 30%, #42a5f5 90%)",
    color: "#ffffff",
    boxShadow: "0 3px 5px 2px rgba(25, 118, 210, .3)",
    transition: "all 0.3s ease-in-out",
    "&:hover": {
      background: "linear-gradient(45deg, #1565c0 30%, #1e88e5 90%)",
      boxShadow: "0 5px 15px rgba(25, 118, 210, .4)",
      transform: "translateY(-1px)",
    },
  };

  // --- RENDER METHOD ---
  return (
    <Box
      sx={{
        background: "#fff",
        p: 2,
        borderRadius: "12px",
        maxHeight: "90vh",
        overflow: "auto",
        "&::-webkit-scrollbar": { width: "6px" },
        "&::-webkit-scrollbar-thumb": { backgroundColor: "#077DD3" },
        "&::-webkit-scrollbar-track": {
          backgroundColor: "rgba(7, 125, 211, 0.24)",
        },
      }}
    >
      {/* --- COMBINED HEADER AND FILTERING SECTION --- */}
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        sx={{
          background: "#fff",
          borderRadius: "12px",
          flexWrap: "wrap",
          gap: 2,
          mb: 2,
        }}
      >
        {/* <Typography sx={{ fontSize: "18px", fontWeight: "600", color: "#000" }}>
          Deleted Income Records
        </Typography> */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Button
            variant="contained"
            size="small"
            startIcon={<FileDownload />}
            onClick={exportToExcel}
            sx={gradientButtonStyle}
          >
            Excel
          </Button>
          <Button
            variant="contained"
            size="small"
            startIcon={<Print />}
            onClick={exportToPDF}
            sx={gradientButtonStyle}
          >
            Print
          </Button>
        </Box>

        <Box
          display="flex"
          gap={1.5}
          alignItems="flex-end"
          flexWrap="wrap"
          justifyContent={{ xs: "flex-start", md: "flex-end" }}
          flexGrow={1}
        >
          <DateRangeSelector
            dateRange={dateRange}
            fromDate={fromDate}
            toDate={toDate}
            onDateRangeChange={handleDateRangeChange}
            onFromDateChange={setFromDate}
            onToDateChange={setToDate}
          />
        </Box>
      </Box>
      <Box>
        {loading ? (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            sx={{ height: "50vh" }}
          >
            <CircularProgress />
          </Box>
        ) : (
          <DynamicTable
            headers={[
              "SN",
              "MRN",
              "Patient Name",
              "Guardian",
              "Gender",
              "Sub Total",
              "Discount",
              "Grand Total",
              "Reason", // <-- Corrected Position
              "Due", // <-- Corrected Position
              "Deleted By",
              "Deleted At",
              "Action",
            ]}
            data={deletedIncomes.map((record, index) => [
              index + 1,
              record.patientInfo?.mrn || "N/A",
              record.patientInfo?.patientName || "N/A",
              record.patientInfo?.guardiansName || "N/A",
              record.patientInfo?.gender || "N/A",
              formatCurrency(record.subTotal),
              formatCurrency(record.discount),
              formatCurrency(record.grandTotal),
              record?.deleteReason || "N/A", // <-- Corrected Position
              formatCurrency(record.due), // <-- Corrected Position
              record.deletedBy || "N/A",
              record.deletedAt
                ? formatDateTime(new Date(record.deletedAt))
                : "N/A",
            ])}
            height={"60vh"}
            actionButtons={[
              {
                icon: (rowIndex: number) => {
                  return (
                    <Tooltip title="Restore invoice" arrow placement="top">
                      <Restore
                        onClick={() => handleRestoreIncome(rowIndex)}
                        sx={{ color: "green", cursor: "pointer" }}
                      />
                    </Tooltip>
                  );
                },
                onClick: (rowIndex: number) => {
                  console.log("Print record at index:", rowIndex);
                },
              },
            ]}
          />
        )}
      </Box>
    </Box>
  );
};

export default DeletedIncome;
